import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { ApiError } from '../api/client';
import type { LoginResultDto } from '../api/types';
import { Field, FormErrors, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

export function LoginPage() {
  const { login, completeTwoFactor, isAuthenticated, booting } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [pending, setPending] = useState<LoginResultDto | null>(null);
  const [code, setCode] = useState('');

  useEffect(() => {
    if (!booting && isAuthenticated) navigate('/', { replace: true });
  }, [booting, isAuthenticated, navigate]);

  const signIn = useMutation({
    mutationFn: () => login({ email, password, rememberMe }),
    onSuccess: (result) => {
      if (result.requiresTwoFactor) {
        setPending(result);
        return;
      }
      navigate('/', { replace: true });
    },
  });

  const verify = useMutation({
    mutationFn: () =>
      authApi.verifyTwoFactor({
        twoFactorToken: pending!.twoFactorToken!,
        code,
        rememberMe,
      }),
    onSuccess: async () => {
      await completeTwoFactor();
      navigate('/', { replace: true });
    },
  });

  const resend = useMutation({
    mutationFn: () => authApi.resendTwoFactorCode({ twoFactorToken: pending!.twoFactorToken! }),
    onSuccess: (message) => toast.success(message ?? 'Kod yeniden gönderildi.'),
    onError: (error) => toast.fromError(error, 'Kod gönderilemedi.'),
  });

  const error = signIn.error;
  const locked = error instanceof ApiError && error.isLocked;

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-50 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-700 text-2xl text-amber-300 shadow-md">
            🌴
          </div>
          <h1 className="text-xl font-bold tracking-tight text-ink-900">Hurmahane Yönetim</h1>
          <p className="mt-1 text-sm text-ink-500">Panele erişmek için giriş yapın.</p>
        </div>

        <div className="card p-6">
          {pending?.twoFactorToken ? (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                verify.mutate();
              }}
            >
              <p className="text-sm text-ink-600">
                Doğrulama kodu {pending.maskedEmail ?? 'e-posta adresinize'} gönderildi.
              </p>

              <FormErrors error={verify.error} />

              <Field label="6 haneli kod" htmlFor="admin-code" required>
                <input
                  id="admin-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  className="field text-center text-lg tracking-[0.4em]"
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                />
              </Field>

              <button type="submit" className="btn-primary w-full" disabled={verify.isPending}>
                {verify.isPending ? <Spinner className="h-4 w-4" /> : null}
                Doğrula
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  className="link"
                  onClick={() => resend.mutate()}
                  disabled={resend.isPending}
                >
                  Kodu yeniden gönder
                </button>
                <button
                  type="button"
                  className="text-ink-500 hover:text-ink-800"
                  onClick={() => setPending(null)}
                >
                  Vazgeç
                </button>
              </div>
            </form>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                signIn.mutate();
              }}
            >
              {locked ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" role="alert">
                  <p className="text-sm font-medium text-amber-900">Hesap geçici olarak kilitli</p>
                  <p className="mt-1 text-sm text-amber-800">{error.errors[0]}</p>
                </div>
              ) : (
                <FormErrors error={error} />
              )}

              <Field label="E-posta" htmlFor="admin-email" required>
                <input
                  id="admin-email"
                  type="email"
                  autoComplete="username"
                  required
                  className="field"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>

              <Field label="Parola" htmlFor="admin-password" required>
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="field"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                />
                Beni hatırla
              </label>

              <button type="submit" className="btn-primary w-full" disabled={signIn.isPending}>
                {signIn.isPending ? <Spinner className="h-4 w-4" /> : null}
                Giriş yap
              </button>

              <p className="text-xs text-ink-500">
                Üç kez hatalı giriş yapılırsa hesap 15 dakika kilitlenir ve bilgilendirme e-postası
                gönderilir.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
